import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Stack, DefaultButton, PrimaryButton, Pivot, PivotItem, MessageBar, MessageBarType, Spinner, Icon, Text } from '@fluentui/react';
import { getContainer, getListFilesAllPages } from '../../../api/admin/api_admin';
import type { ContainerInfo, FileInfo } from '../../../api/admin/models_admin';
import { AdminLabel, ErrorDialog, ConfirmDialog, naturalCollator } from "../components";
import { UpdateTagsSubcategoryPanel, type TagRow } from './update_tags_subcategory_panel';
import { UpdateTagsFilePanel } from './update_tags_file_panel';
import { ApplyTagsModal, ApplyTagsResult } from './apply_tags_modal';
import { buildTagDefinitionChange, buildTagFileChanges, validateTags } from './tag_changes';
import { IndexerLockMessageBar, isLockedByIndexer } from './indexer_lock';
import { useBeforeUnloadGuard } from '../../../hooks/useBeforeUnloadGuard';

export const UpdateTags: React.FC = () => {
    const { seq } = useParams<{ seq: string }>();
    const [data, setData] = useState<ContainerInfo | null>(null);
    const [files, setFiles] = useState<FileInfo[]>([]);
    const [loading, setLoading] = useState(true);
    // ファイルはページ単位で全件取得するため、進捗を出して待ち時間の目安を示す
    const [loadProgress, setLoadProgress] = useState<{ loaded: number, total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [selectedTabKey, setSelectedTabKey] = useState<string>('container');

    // ===== パネルの編集状態 =====
    // Pivotは非表示のPivotItemをアンマウントするため、タブを切り替えても
    // 未反映の編集内容が失われないよう、パネルの状態はすべて親で保持する
    const [tags, setTags] = useState<TagRow[]>([]);
    const [fileTagKeys, setFileTagKeys] = useState<Record<number, string[]>>({});
    const [originalFileTagKeys, setOriginalFileTagKeys] = useState<Record<number, string[]>>({});
    const [editingTagKey, setEditingTagKey] = useState<string | undefined>(undefined);
    const [fileNameFilter, setFileNameFilter] = useState('');
    // これまでに払い出したコンテナタグキーの最大値。削除しても減らさず、キーの再利用を防ぐ
    const maxAssignedKey = useRef(0);

    // 「編集を完了」まで実行を保留している変更の実行モーダル
    const [applying, setApplying] = useState(false);

    const fetchData = async () => {
        if (!seq) return;
        try {
            setLoading(true);
            setLoadProgress(null);
            // この画面のファイル名フィルタ・サブカテゴリの一括付与・変更差分の算出はいずれも
            // 全ファイルがそろっている前提のため、部分ロードにはせず全件を読み切る
            const [containerResult, filesResult] = await Promise.all([
                getContainer(Number(seq)),
                getListFilesAllPages(Number(seq), (loaded, total) => setLoadProgress({ loaded, total })),
            ]);
            let loadedTags: TagRow[] = [];
            if ("error" in containerResult) {
                setError(containerResult.error);
            } else {
                setData(containerResult);
                loadedTags = (containerResult.tags ?? []).sort((t1, t2) => naturalCollator.compare(t1.value, t2.value)).map(t => ({ key: t.key, value: t.value }));
                setTags(loadedTags);
                // 既存キーの最大値をカウンタの初期値にする。
                // 未保存のキーを払い出した後に再取得する場合があるため、カウンタは決して戻さない
                maxAssignedKey.current = loadedTags.reduce((max, t) => {
                    const n = parseInt(t.key, 10);
                    return Number.isNaN(n) ? max : Math.max(max, n);
                }, maxAssignedKey.current);
            }
            if ("error" in filesResult) {
                setError(filesResult.error);
            } else {
                setFiles(filesResult.files);
                // ファイルのタグキーはコンテナタグ (loadedTags) と同じ並び順に揃える。
                const tagKeyOrder = new Map(loadedTags.map((t, i) => [t.key, i]));
                const sortTagKeys = (keys: string[]): string[] =>
                    [...keys].sort((k1, k2) =>
                        (tagKeyOrder.get(k1) ?? Number.MAX_SAFE_INTEGER) - (tagKeyOrder.get(k2) ?? Number.MAX_SAFE_INTEGER)
                    );
                const initialTagKeys = Object.fromEntries(
                    filesResult.files.map(f => [f.seq, sortTagKeys(f.tag_keys ?? [])])
                );
                setFileTagKeys(initialTagKeys);
                setOriginalFileTagKeys(initialTagKeys);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [seq]);

    // 未実行の変更。ファイル側は、削除されたサブカテゴリの残骸を差分に数えないよう正規化して比較する
    const definitionChange = buildTagDefinitionChange(data?.tags ?? [], tags);
    const fileChanges = buildTagFileChanges(files, fileTagKeys, originalFileTagKeys, tags);
    const hasPendingChange = definitionChange !== null || 0 < fileChanges.length;

    // 変更を実行しないまま閉じられると編集内容が失われるため、離脱前に確認する
    const { closeWindow } = useBeforeUnloadGuard(hasPendingChange);

    // キーは1始まりの自然数。追加時は「これまで払い出した最大値」+1で採番し、
    // 削除しても番号を戻さないことで、削除済みキーの再利用（＝別の値への付け替え）を防ぐ
    const nextTagKey = (): string => {
        maxAssignedKey.current += 1;
        return String(maxAssignedKey.current);
    };

    const handleApplyClick = () => {
        // 「登録」ボタンを廃止して一括実行に寄せたため、入力の検証はここで行う
        const validationError = validateTags(tags);
        if (validationError !== null) {
            setError(validationError);
            setSelectedTabKey('container');
            return;
        }
        setApplying(true);
    };

    const handleApplyFinished = async (result: ApplyTagsResult) => {
        setApplying(false);

        // 実行に入らずキャンセルされた場合は、編集内容をそのまま残して編集を続けられるようにする
        if (!result.executed) return;

        const hasFailure = result.definitionFailed || 0 < result.failedFiles.length;
        if (result.indexerStarted && !hasFailure) {
            // 変更は実行し終えたため、確認なしで閉じる
            if (closeWindow()) return;
            // スクリプトから開いたタブでなければ閉じられないため、結果を示して一覧を最新化する
            setSuccess(`${result.message}
このタブは閉じて構いません。`);
            await fetchData();
            return;
        }

        // 失敗が残っている間は閉じずに、一覧を最新化して編集を継続できるようにする
        if (result.reloadRequired) {
            await fetchData();
            // 実行できなかった変更は編集内容へ戻し、やり直せるようにする
            if (0 < result.failedFiles.length) {
                setFileTagKeys(prev => {
                    const next = { ...prev };
                    for (const change of result.failedFiles) {
                        next[change.file.seq] = change.tagKeys;
                    }
                    return next;
                });
            }
        }

        if (result.message !== "") {
            setError(result.message);
        }
    };

    if (loading) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <Spinner label={
                    loadProgress && 0 < loadProgress.total
                        ? `読み込み中... (${loadProgress.loaded}/${loadProgress.total}件)`
                        : "読み込み中..."
                } />
            </Stack>
        );
    }

    if (!data) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    データが見つかりません
                </MessageBar>
                <DefaultButton text="閉じる" onClick={() => window.close()} styles={{ root: { marginTop: 20, width: 'fit-content' } }} />
            </Stack>
        );
    }

    const isEditable = !isLockedByIndexer(data.is_indexer_running) && !applying;

    return (
        <Stack tokens={{ padding: 20, childrenGap: 20 }}>
            <AdminLabel text={`サブカテゴリ設定 ＜${data.container_name}＞`} />

            {
                <ErrorDialog
                    content={error ?? ""}
                    onDismiss={() => setError(null)}
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog
                    content={success ?? ""}
                    onDismiss={() => setSuccess(null)}
                    isOpen={Boolean(success)}
                    title="確認" />
            }

            <IndexerLockMessageBar isIndexerRunning={data.is_indexer_running} />

            {data.is_tag_usable !== null && !data.is_tag_usable ?
                <MessageBar messageBarType={MessageBarType.warning}>
                    このカテゴリはサブカテゴリ機能に対応していません。
                    カテゴリ一覧画面で文書検索設定を解除したうえで再設定し、インデックスを作成し直してください。
                </MessageBar>
                :
                <>
                    {/* 編集を完了。タブによらず、この画面の変更をまとめて実行する */}
                    <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                        <PrimaryButton
                            text="編集を完了して検索へ反映"
                            onClick={handleApplyClick}
                            disabled={!hasPendingChange || !isEditable}
                        />
                        {hasPendingChange && (
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                <Icon iconName="Info" styles={{ root: { color: '#605e5c', fontSize: 14 } }} />
                                <Text variant="small">
                                    未実行の変更があります（
                                    サブカテゴリの定義: {definitionChange === null ? "変更なし" : "変更あり"} /
                                    ファイル: {fileChanges.length}件
                                    ）。「編集を完了」を押すまで実行されません。
                                </Text>
                            </Stack>
                        )}
                    </Stack>

                    <Pivot
                        selectedKey={selectedTabKey}
                        onLinkClick={item => {
                            if (item?.props.itemKey) setSelectedTabKey(item.props.itemKey);
                        }}
                    >
                        {/* サブカテゴリ設定 */}
                        <PivotItem itemKey="container" headerText="サブカテゴリ作成">
                            <UpdateTagsSubcategoryPanel
                                disabled={!isEditable}
                                tags={tags}
                                setTags={setTags}
                                nextTagKey={nextTagKey}
                            />
                        </PivotItem>

                        {/* ファイル設定 */}
                        <PivotItem itemKey="file" headerText="ファイル設定">
                            <UpdateTagsFilePanel
                                disabled={!isEditable}
                                // 未保存のサブカテゴリもそのまま付与できるよう、編集中の定義を渡す
                                containerTags={tags}
                                files={files}
                                fileTagKeys={fileTagKeys}
                                setFileTagKeys={setFileTagKeys}
                                editingTagKey={editingTagKey}
                                setEditingTagKey={setEditingTagKey}
                                fileNameFilter={fileNameFilter}
                                setFileNameFilter={setFileNameFilter}
                            />
                        </PivotItem>
                    </Pivot>
                </>
            }

            {/* 保留中の変更の実行。開くたびに状態をリセットするため、閉じている間はマウントしない */}
            {applying && (
                <ApplyTagsModal
                    containerSeq={data.seq}
                    containerName={data.container_name}
                    definitionChange={definitionChange}
                    fileChanges={fileChanges}
                    tagValueByKey={new Map(tags.map(t => [t.key, t.value]))}
                    onFinished={handleApplyFinished}
                />
            )}
        </Stack>
    );
};
